import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _isEqual from 'lodash/isEqual';

import InfiniteScroller from 'components/InfiniteScroller';
import { searchAlgolia } from 'api/algolia';

/**
 * [AlgoliaPagination]
 * Provide render method with props to handle pagination.  Can manage multiple data / pagination
 * states by passing a different cacheName. This is useful for searching results that aren't
 * currently displayed on page.
 *
 * @prop {object} searchParams - contains search params to pass to algolia via 'searchAlgolia'
 * @prop {bool} infiniteScroll - if true, render children inside InfiinteScroller component
 * @prop {func} children - render function that receives prop from this component
 * @prop {string} cacheName - in the event that you want AlgoliaPagination to manage pagination
 * state for multiple scenarios, just provide the new cache name with the new search params
 */

const INITIAL_PAGINATION_STATE = {
  isLoading: true,
  page: 0,
  nbHits: 0,
  nbPages: 0,
  limit: 100,
  data: [],
  lastFetched: null,
  limitReached: false,
  params: {} // store last used search params for given cache
};

class AlgoliaPagination extends Component {
  static defaultProps = {
    cacheName: 'default',
    searchParams: {}
  };

  state = {};

  // Called before render (including initial mount)
  static getDerivedStateFromProps(props, state) {
    // If new cacheName provided, make sure state for it is initialized
    // Need to initialize state prior to render, so we do so here
    if (!state[props.cacheName]) {
      return {
        [props.cacheName]: INITIAL_PAGINATION_STATE
      };
    }
    return null;
  }

  componentDidMount() {
    const initialState = {
      ...INITIAL_PAGINATION_STATE,
      params: this.props.searchParams
    };
    this._setCacheState(initialState, () => this._fetchDataAlgolia());
  }

  componentDidUpdate() {
    // if searchParams update from last used search params for this cache, restart pagination.
    // if new cacheName is passed, restart will get called and will intialize the cache state
    const { params } = this._getCacheState();
    if (_isEqual(params, this.props.searchParams)) return;
    this._restart();
  }

  _restart = () => {
    const initialState = {
      ...INITIAL_PAGINATION_STATE,
      params: this.props.searchParams
    };

    this._setCacheState(initialState, () => this._fetchDataAlgolia());
  };

  _getCacheState = () => {
    const { cacheName } = this.props;
    return this.state[cacheName];
  };

  _setCacheState = (stateArgs, callback, _cacheName) => {
    const cacheName = _cacheName || this.props.cacheName;
    const cacheState = this._getCacheState() || {};
    this.setState(
      {
        [cacheName]: {
          ...cacheState,
          ...stateArgs
        }
      },
      callback
    );
  };

  _fetchDataAlgolia = () => {
    const { searchParams, cacheName } = this.props;

    const cacheState = this._getCacheState();
    const { page, limit, nbPages, nbHits } = cacheState;

    // NOTE: if request already in progress, we don't block. It's up to invoking parent to throttle accordingly
    this._setCacheState({ isLoading: true, params: searchParams });

    // page starts at 0, don't fetch if reached the end.
    if (nbPages > 0 && page >= nbPages - 1) return Promise.resolve();
    const nextPage = page === nbPages ? page : page + 1;

    return searchAlgolia({ limit, page: nextPage, ...searchParams })
      .then(response => {
        // if the index changed or mixes is missing (usually due
        // to network error), re-fetch from the first page
        if ((nbHits > 0 && nbHits !== response.nbHits) || !response.hits) {
          this._restart();
          return;
        }

        const data = [...cacheState.data, ...response.hits];

        this._setCacheState(
          {
            data,
            lastFetched: Date.now(),
            page: response.page,
            limit: response.limit,
            nbHits: response.nbHits,
            nbPages: response.nbPages,
            isLoading: false,
            limitReached: response.page >= response.nbPages - 1
          },
          null,
          cacheName // Pass cacheName since props could have changed while algolia results were fetching
        );
      })
      .catch(e => {
        this._setCacheState({
          isLoading: false,
          error: e.message
        });
      });
  };

  render() {
    const { data, lastFetched, isLoading, limitReached, error } = this._getCacheState();
    const { children, infiniteScroll } = this.props;

    const childrenProps = {
      data,
      lastFetched,
      loading: isLoading,
      limitReached,
      error
    };

    if (infiniteScroll) {
      return (
        <InfiniteScroller hasMore={!limitReached} fetchMoreData={this._fetchDataAlgolia}>
          {children(childrenProps)}
        </InfiniteScroller>
      );
    }

    return children(childrenProps);
  }
}

AlgoliaPagination.propTypes = {
  infiniteScroll: PropTypes.bool,
  children: PropTypes.func.isRequired,
  searchParams: PropTypes.shape(),
  cacheName: PropTypes.string
};

export default AlgoliaPagination;
